import React, { FC, useState, useEffect } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { nextApiRequest } from '../../lib/utils';
import { WWWCompanyDocument } from '../../common/documents/WWWCompanyDocument';
import { convertCompanyDocumentToEditable } from './companyEditCommon';
import { companyEditState, convertEditableToState } from '../../context/companyEditStore';
import { Error } from '../Error';
import { Button } from '../Button';
import { Dialog, StyledDialog } from '../common/StyledDialog';
import { useScopedState } from '../../lib/state';
import { handleBasicErrors } from '../../context/basicErrorHandling';
import { gaEvent } from '../../lib/ga';

export const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  > .MuiFormControl-root {
    margin-top: 2rem;
  }

  > button {
    margin-top: 1.2rem;
  }

  @media (max-width: 640px) {
    flex: 1;
  }
`;

const Select = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 640px) {
    flex: 1;
  }
`;

export const Control = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

export interface CompanyEditDialogContentProps {
  company: WWWCompanyDocument;
  openSelect: (component: JSX.Element) => void;
  closeSelect: () => void;
  disableSave: (disabled: boolean) => void;
  setControl: (component: JSX.Element) => void;
  setError: (error: JSX.Element | null) => void;
  close: () => void;
  save: () => Promise<void>;
}

export type CompanyEditDialogContent = FC<CompanyEditDialogContentProps>;

export interface CompanyEditDialogProps {
  open: boolean;
  company: WWWCompanyDocument;
  content: React.ComponentType<CompanyEditDialogContentProps> | null;
  onUpdate: (document: WWWCompanyDocument) => void;
  onClose: () => void;
}

export const CompanyEditDialog: FC<CompanyEditDialogProps> = ({ open, company, content: ContentComponent, onUpdate, onClose }) => {
  const { set, doGetEditRequestPayload } = useScopedState(companyEditState);
  const [selectComponent, setSelectComponent] = useState<JSX.Element | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<JSX.Element | null>(null);
  const [control, setControl] = useState<JSX.Element | null>(null);
  const [disabled, setDisabled] = useState(false);
  const intl = useIntl();

  /** Convert latest company data to editable form when the dialog is opened */
  useEffect(() => {
    if (open) {
      set(convertEditableToState(convertCompanyDocumentToEditable(company)));
      setControl(null);
      setDisabled(null);
      setError(null);
      setLoading(null);
    }
  }, [open]);

  /** Collect edited data and send the API request */
  const handleEdit = async () => {
    try {
      setLoading(true);
      setError(null);

      const { status, payload } = await doGetEditRequestPayload({ company });

      // Something was changed, send the API request
      gaEvent('edit_company', { event_category: 'engagement', event_label: 'save' });
      if (status !== 'nothing changed') {
        const { status, company: updatedDocument } = await nextApiRequest('/api/v1/www/company/edit', payload);

        const errorStatus = handleBasicErrors({ status, catchAll: true });
        if (errorStatus) {
          return setError(errorStatus.error);
        }

        // Update the loaded company document after it has been saved
        onUpdate(updatedDocument);
        onClose();
      } else {
        onClose();
      }
    } catch (error) {
      console.error(error);
      setError(<FormattedMessage id="unknownError" defaultMessage="Jokin meni vikaan, yritä myöhemmin uudelleen…" />);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousClick = async () => {
    if (!!selectComponent) {
      handleSelectClose();
    } else {
      const { status } = await doGetEditRequestPayload({ company });

      if (status !== 'nothing changed') {
        // Ask the user if they really want to close the modal with pending changes
        const confirmText = intl.formatMessage({
          id: 'company.editDialog.confirmExitText',
          defaultMessage: 'Sinulla on tallentamattomia muutoksia. Oletko varma että haluat lopettaa muokkaamisen ilman tallennusta?',
        });

        if (window.confirm(confirmText)) {
          onClose();
        }
      } else {
        onClose();
      }
    }
  };

  const handleSaveClick = () => {
    if (!!selectComponent) {
      handleSelectClose();
    } else {
      handleEdit().catch(console.error);
    }
  };

  const handleSelectOpen = (component: JSX.Element) => {
    setSelectComponent(component);
  };

  const handleSelectClose = () => {
    setSelectComponent(null);
  };

  const handleSaveDisable = (disabled: boolean) => {
    setDisabled(disabled);
  };

  const handleControlSet = (component: JSX.Element) => {
    setControl(component);
  };

  const handleClose = (_, reason: string) => {
    if (reason === 'backdropClick') return;
    if (onClose) onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose} aria-labelledby="simple-modal-title" aria-describedby="simple-modal-description">
      <Dialog>
        <Content style={{ display: selectComponent ? 'none' : 'flex' }}>
          {ContentComponent && (
            <ContentComponent
              company={company}
              openSelect={handleSelectOpen}
              closeSelect={handleSelectClose}
              disableSave={handleSaveDisable}
              setControl={handleControlSet}
              setError={setError}
              close={handlePreviousClick}
              save={handleEdit}
            />
          )}
        </Content>

        <Select style={{ display: !selectComponent ? 'none' : 'flex' }}>{selectComponent}</Select>

        <Error error={error} />

        <Control>
          {control}

          {!control && (
            <>
              <Button variant="text" color="default" disabled={loading} onClick={handlePreviousClick}>
                {!!selectComponent ? 'Takaisin' : 'Sulje'}
              </Button>
              <Button loading={loading || disabled} onClick={handleSaveClick}>
                {!!selectComponent ? 'OK' : 'Tallenna'}
              </Button>
            </>
          )}
        </Control>
      </Dialog>
    </StyledDialog>
  );
};
